// src/pages/Sensor/components/Benchmarks.tsx

import { AddTwoTone } from '@mui/icons-material'
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { SensorId } from '@/types/domain'
import { ContractId, ContractPool } from '@/types/domain/contract'

import { DDMMYYYY, userLocales } from '../../../api'
import { HStack, KTextField } from '../../../components'
import { useBenchmark, useContractPool, useBenchmarkApi } from '../../../hooks'

const GermanDate = data =>
  new Date(data).toLocaleDateString(userLocales, DDMMYYYY)

const germanNotation = value => value && value.toString().replace(',', '.')

const removeEntityId = obj => {
  const newObj = { ...obj }
  if ('entity_id' in newObj) delete newObj.entity_id

  return newObj
}

type Props = {
  sensor: {
    entity_id: SensorId
  }
  contractId?: ContractId
}

export const Benchmarks = (sensor: Props) => {
  const navigate = useNavigate()

  const contractPool: ContractPool = useContractPool()

  const { remove, save, update } = useBenchmarkApi()

  const { entity_id } = sensor.sensor

  const { contractId } = sensor

  const contractData = contractPool.find(
    f => Number(f.id) === Number(contractId)
  )

  let date_begin = '2018-01-01'
  let date_expire = new Date(Date.now()).toISOString().split('T')[0]
  if (contractData && contractData.attributes) {
    date_begin = contractData.attributes.date_begin
    date_expire = contractData.attributes.date_expire
  }
  // limit date selection to contract lifetime
  const boundaries = date =>
    date < date_begin ? date_begin : date > date_expire ? date_expire : date
  const sanitize = date => boundaries(date)
  const { data: benchmarks } = useBenchmark(entity_id)

  const isLoading = benchmarks?.length === 0
  const [editMode, setEditMode] = useState(false)
  const [newBenchmark, setNewBenchmark] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [warningText, setWarningText] = useState<string | false>(false)

  const basicBenchmark = {
    period_start: sanitize(date_begin),
    period_end: sanitize(date_expire),
    benchmark_name: null,
    value: null,
    unit: null,
    sensor_id: entity_id,
  }

  const [benchmark, setBenchmark] = useState(basicBenchmark)

  useEffect(() => {
    if (isLoading) return
    if (benchmarks.length > 0) {
      const lastBenchmark = benchmarks.at(-1)
      lastBenchmark.attributes.entity_id = benchmarks.at(-1).id
      setBenchmark(lastBenchmark.attributes)
    }
  }, [isLoading, benchmarks])

  useEffect(() => {
    if (isLoading) return
    if (benchmarks.length > 1) {
      const checkSensorFactorsPeriodsOverlap = periods => {
        periods.sort(
          (a, b) =>
            new Date(a.period_start).getTime() -
            new Date(b.period_start).getTime()
        )
        for (let i = 1; i < periods.length; i++) {
          if (
            new Date(periods[i].period_start) <
            new Date(periods[i - 1].period_end)
          )
            return true
        }
        return false
      }
      const checkForPeriodOverlap = checkSensorFactorsPeriodsOverlap(benchmarks)
      if (checkForPeriodOverlap) {
        setShowWarning(true)
        setWarningText(
          'Es gibt Überschneidungen zwischen den gesetzten Zeitäumen'
        )
      } else {
        setShowWarning(false)
      }
    }
  }, [isLoading, benchmarks])

  useEffect(() => {
    if (!sensor?.sensor?.entity_id) return
    const sId: SensorId = sensor.sensor.entity_id

    setBenchmark(prev => ({ ...prev, sensor_id: sId }))
  }, [sensor.sensor])

  const saveBenchmark = benchmark =>
    newBenchmark === true
      ? save(removeEntityId(benchmark))
      : update(benchmark, benchmark.entity_id)

  const deleteBenchmark = benchmark => remove(benchmark.entity_id)

  const handleRowClick = rowInfo => {
    setEditMode(true)
    setNewBenchmark(false)
    setBenchmark(rowInfo.attributes)
  }

  return (
    <>
      <Dialog
        maxWidth={'lg'}
        open={editMode}
        onClose={() => {
          setEditMode(false)
        }}
      >
        <DialogTitle>
          {newBenchmark === true
            ? 'Hier können Sie neue Benchmarks für einen gewünschten Zeitraum erfassen'
            : 'Benchmarks für Zeitraum bearbeiten'}
        </DialogTitle>
        <DialogContent>
          {' '}
          <HStack>
            <TextField
              label={'Beginn Zeitraum'}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: sanitize(date_begin),
                max: sanitize(date_expire),
              }}
              onChange={event =>
                setBenchmark(prev => ({
                  ...prev,
                  period_start: event.target.value,
                }))
              }
              type={'date'}
              value={benchmark.period_start}
              fullWidth
            />
            <TextField
              label={'Ende Zeitraum'}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: sanitize(date_begin),
                max: sanitize(date_expire),
              }}
              onChange={e =>
                setBenchmark(prev => ({
                  ...prev,
                  period_end: e.target.value,
                }))
              }
              type={'date'}
              value={benchmark.period_end}
              fullWidth
            />
            <KTextField
              error={!benchmark.benchmark_name}
              inputProps={{ min: 0 }}
              label={'Name'}
              onChange={e =>
                setBenchmark(prev => ({
                  ...prev,
                  benchmark_name: e.target.value,
                }))
              }
              type={'string'}
              value={germanNotation(benchmark.benchmark_name)}
            />
            <KTextField
              label={'Wert'}
              onChange={event => {
                setBenchmark(prev => ({
                  ...prev,
                  value: event.target.value,
                }))
              }}
              type={'number'}
              value={germanNotation(benchmark.value)}
            />
            <KTextField
              error={!benchmark.unit}
              label={'Einheit'}
              onChange={event =>
                setBenchmark(prev => ({
                  ...prev,
                  unit: event.target.value,
                }))
              }
              type={'string'}
              value={germanNotation(benchmark.unit)}
            />
          </HStack>{' '}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditMode(false)
            }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => {
              setEditMode(false)
              deleteBenchmark(benchmark)
              navigate('/sensors')
            }}
          >
            Löschen
          </Button>
          <Button
            onClick={() => {
              setEditMode(false)
              saveBenchmark(benchmark)
              navigate('/sensors')
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
      <Collapse in={showWarning}>
        <Alert severity={'warning'}>{warningText}</Alert>
      </Collapse>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Beginn Zeitraum</TableCell>
              <TableCell>Ende Zeitraum</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Wert</TableCell>
              <TableCell>Einheit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {benchmarks
              ?.filter(f => f.attributes.sensor_id === entity_id)
              ?.map(benchmark => (
                <TableRow
                  key={benchmark.id}
                  hover
                  onClick={() => handleRowClick(benchmark)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {GermanDate(benchmark.attributes.period_start)}
                  </TableCell>
                  <TableCell>
                    {GermanDate(benchmark.attributes.period_end)}
                  </TableCell>
                  <TableCell>{benchmark.attributes.benchmark_name}</TableCell>
                  <TableCell>{benchmark.attributes.value}</TableCell>
                  <TableCell>{benchmark.attributes.unit}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <IconButton
        onClick={() => {
          setEditMode(true)
          setNewBenchmark(true)
        }}
      >
        <AddTwoTone />
      </IconButton>
    </>
  )
}
